import {
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { TopBar } from "./top-bar";
import { ColorSlider } from "./color-slider";
import { ThicknessBar } from "./thickness-bar";
import { CropOverlay } from "./crop-overlay";
import type { CropRect } from "./crop-overlay";
import { InlineTextEditor } from "./inline-text-editor";
import type { InlineTextEditorHandle } from "./inline-text-editor";
import { TextOverlay } from "./text-overlay";
import { useMarkupHistory } from "./use-markup-history";
import {
    renderNonTextDrawings,
    generateId,
    getPointFromEvent,
    measureTextDrawing,
} from "./canvas-utils";
import {
    addTurns,
    applyRotationTransform,
    getRotatedSize,
    normalizeTurns,
    turnDrawings,
    turnRectBy,
    type Rotation,
} from "./rotation";
import * as Sentry from "@sentry/react";

import { renderAnnotatedPair } from "./render-annotated-blob";
import { ShapePickerBar } from "./shape-picker-bar";
import {
    BASE_TEXT_FONT_SIZE,
    type ToolType,
    type Drawing,
    type Point,
    type PenDrawing,
    type TextDrawing,
    type ShapeDrawing,
    type ShapeType,
    type PenThickness,
} from "./types";

export interface MarkupCompletePayload {
    originalFile: File;
    /**
     * JPEG bytes the client has already rendered with the drawings + crop
     * composited — the server stores these as ProjectImage.image directly
     * (no round-trip through the Pillow renderer). Same pixels the preview
     * canvas shows.
     */
    annotatedBlob: Blob;
    /**
     * The same rendered image capped at ~512px on its longest edge, produced
     * from the SAME composite pass as `annotatedBlob` (see
     * [[render-annotated-blob]]). Hosts paint tiles and seed caches from this,
     * never from `annotatedBlob`: a ~120px tile handed a multi-megapixel JPEG
     * pays a full decode to paint it and keeps those bytes resident for every
     * such tile on screen. Upload still uses the full-resolution blob.
     */
    thumbnailBlob: Blob;
    drawings: Drawing[];
    crop?: CropRect;
    /**
     * Clockwise quarter-turns the user applied to the photo. Already baked into
     * `annotatedBlob`, but it MUST be persisted alongside `drawings`/`crop`:
     * the re-edit flow re-opens the unrotated `originalFile`, so without it the
     * seeded drawings would land against the wrong pixel grid. Omitted when 0.
     */
    rotation?: Rotation;
    downscaleMaxPx?: number;
}

/**
 * Imperative handle exposed via `ref`. Lets a host (the burst-camera
 * orchestrator) flush the editor's current edits into a `MarkupCompletePayload`
 * WITHOUT pressing Done — used to persist the in-progress photo before the
 * filmstrip switches to another. Returns `null` when there is nothing to render
 * (no `imageFile`). See [[burst-camera.spec]] "commit-on-switch".
 */
export interface ImageMarkupEditorHandle {
    /** Render the current drawings + crop into a payload (same pipeline as Done), without calling `onComplete`. */
    commitToPayload: () => Promise<MarkupCompletePayload | null>;
}

export interface ImageMarkupEditorProps {
    isOpen: boolean;
    onClose: () => void;
    imageFile: File | null;
    onComplete: (payload: MarkupCompletePayload) => void;
    projectName?: string;
    /**
     * Phase 2 re-edit hydration: pre-existing drawings to seed the editor
     * history with when opening on an already-submitted record. Applied on
     * mount of a given `imageFile` — reset alongside history when `imageFile`
     * changes. Absent / empty for the fresh-capture flow.
     */
    initialDrawings?: Drawing[];
    /** Phase 2 re-edit hydration: pre-existing crop rectangle, in rotation-corrected coords. */
    initialCrop?: CropRect | null;
    /**
     * Phase 2 re-edit hydration: the rotation the record was submitted with.
     * The re-edit path hands us the UNROTATED original, so this is what puts
     * the photo back the right way up and lines the seeded drawings up with it.
     */
    initialRotation?: Rotation;
    /**
     * Optional navigator rendered between the markup stage and the bottom
     * controls — used by the burst camera to show a filmstrip of the batch
     * (see [[burst-camera.spec]]). Hidden while cropping, matching the bottom
     * bar. Absent for the single-capture / re-edit flows.
     */
    filmstripSlot?: React.ReactNode;
}

const MAX_CANVAS_FONT_SIZE = 2000;

interface ViewTransform {
    scale: number;
    offsetX: number;
    offsetY: number;
}

function getCanvasScale(canvas: HTMLCanvasElement): number {
    // Use explicit CSS dimensions (set during canvas setup) rather than
    // getBoundingClientRect() which fluctuates during CSS transform changes,
    // causing text overlay positions to jitter/quiver during zoom.
    const displayWidth = parseFloat(canvas.style.width) || 0;
    const displayHeight = parseFloat(canvas.style.height) || 0;
    if (displayWidth > 0 && displayHeight > 0) {
        return Math.max(canvas.width / displayWidth, canvas.height / displayHeight);
    }
    // Fallback before canvas dimensions are set
    const rect = canvas.getBoundingClientRect();
    return Math.max(canvas.width / rect.width, canvas.height / rect.height);
}

export const ImageMarkupEditor = forwardRef<
    ImageMarkupEditorHandle,
    ImageMarkupEditorProps
>(function ImageMarkupEditor(
    {
        isOpen,
        onClose,
        imageFile,
        onComplete,
        projectName,
        initialDrawings,
        initialCrop,
        initialRotation,
        filmstripSlot,
    },
    ref,
) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerMounted, setContainerMounted] = useState(false);
    const containerCallbackRef = useCallback((node: HTMLDivElement | null) => {
        containerRef.current = node;
        setContainerMounted(!!node);
    }, []);
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const imageSrcRef = useRef<string | null>(null);
    const inlineTextEditorRef = useRef<InlineTextEditorHandle>(null);
    const suppressBlurRef = useRef(false);
    const keyboardProxyRef = useRef<HTMLInputElement>(null);

    // Zoom/pan gesture refs
    const pinchRef = useRef<{ initialDistance: number; initialScale: number; midX: number; midY: number; initialOffsetX: number; initialOffsetY: number } | null>(null);
    const textResizePinchRef = useRef<{ initialDistance: number; initialFontSize: number; initialMaxWidth: number; textId: string; currentScale: number; initialDisplayHeight: number } | null>(null);
    const panRef = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(null);
    const isZoomingRef = useRef(false);
    const lastTapTimeRef = useRef(0);
    const viewTransformRef = useRef<ViewTransform>({ scale: 1, offsetX: 0, offsetY: 0 });

    const [selectedTool, setSelectedTool] = useState<ToolType | null>("pen");
    const selectedToolRef = useRef<ToolType | null>("pen");
    const [selectedColor, setSelectedColor] = useState<string>("#EF4444");
    const [penThickness, setPenThickness] = useState<PenThickness>(6);
    const [selectedShapeType, setSelectedShapeType] = useState<ShapeType>("rectangle");
    const [shapeThickness, setShapeThickness] = useState<PenThickness>(6);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentDrawing, setCurrentDrawing] = useState<Drawing | null>(null);
    const [cropRect, setCropRect] = useState<CropRect | null>(null);
    const [rotation, setRotation] = useState<Rotation>(initialRotation ?? 0);
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    // Surfaced next to Done when the composite fails (undecodable source, a 2D
    // context the device won't give us, a canvas allocation it refuses).
    const [exportError, setExportError] = useState<string | null>(null);
    const [canvasReady, setCanvasReady] = useState(false);
    const [decodeError, setDecodeError] = useState(false);
    const [canvasScale, setCanvasScale] = useState(1);
    // Canvas geometry mirrored into state. Reading it off `canvasRef` during
    // render goes stale the moment a rotation swaps the canvas dimensions —
    // the layout effect mutates the element, which re-renders nothing.
    const [canvasSize, setCanvasSize] = useState<{
        width: number;
        height: number;
        displayWidth: number;
        displayHeight: number;
    } | null>(null);
    const [viewTransform, setViewTransform] = useState<ViewTransform>({ scale: 1, offsetX: 0, offsetY: 0 });
    const currentDrawingRef = useRef<Drawing | null>(null);
    const addDrawingRef = useRef<typeof addDrawing>(null!);
    viewTransformRef.current = viewTransform;
    selectedToolRef.current = selectedTool;

    // Interactive text state
    const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
    const selectedTextIdRef = useRef<string | null>(null);
    selectedTextIdRef.current = selectedTextId;
    const [fontSize] = useState(BASE_TEXT_FONT_SIZE);

    // Inline text editing state
    // isAddingText: true when the centered text editor is shown for new text
    const [isAddingText, setIsAddingText] = useState(false);
    // editingTextId: ID of text being re-edited (tap-to-edit)
    const [editingTextId, setEditingTextId] = useState<string | null>(null);
    const isInlineEditing = isAddingText || editingTextId !== null;
    const isCropMode = selectedTool === "crop";

    const {
        drawings,
        addDrawing,
        commitDrawingUpdate,
        undo,
        canUndo,
        clear: clearHistory,
        reset: resetHistory,
        transformAll,
    } = useMarkupHistory(initialDrawings);
    currentDrawingRef.current = currentDrawing;
    addDrawingRef.current = addDrawing;
    const drawingsRef = useRef(drawings);
    drawingsRef.current = drawings;

    // Derive text drawings for overlay rendering
    const textDrawings = drawings.filter(
        (d): d is TextDrawing => d.type === "text"
    );

    // Clear selected text when it no longer exists (e.g. after undo)
    useEffect(() => {
        if (selectedTextId && !drawings.some((d) => d.id === selectedTextId)) {
            setSelectedTextId(null);
        }
    }, [drawings, selectedTextId]);

    // Reset drawings + crop when a different image is loaded. Re-opening the
    // editor for the SAME imageFile (re-edit flow from preview modal)
    // preserves history so the user sees their existing markups.
    //
    // Phase 2: when the host supplies `initialDrawings` / `initialCrop`
    // (re-edit on an existing server record), seed the fresh history and
    // crop state with them. This is the only path that rehydrates pre-set
    // annotations; the fresh-capture path leaves both unset.
    useEffect(() => {
        resetHistory(initialDrawings);
        setCropRect(initialCrop ?? null);
        setRotation(initialRotation ?? 0);
        // Intentionally excluded from deps: `initialDrawings`/`initialCrop`
        // are captured at imageFile identity. The host keeps the editor
        // mounted across Done round-trips only within a single imageFile.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageFile, resetHistory]);

    // Reset to pen mode each time the editor opens
    useEffect(() => {
        if (isOpen) {
            setSelectedTool("pen");
            setSelectedColor("#EF4444");
            setSelectedTextId(null);
        }
    }, [isOpen]);

    // Handle tool changes
    const handleToolChange = useCallback((tool: ToolType | null) => {
        // If currently editing text, trigger confirm (saves text if non-empty)
        if (isAddingText || editingTextId) {
            inlineTextEditorRef.current?.confirmOrCancel();
        }

        setSelectedTool(tool);

        if (tool === "text") {
            // Focus proxy input immediately during user gesture to raise mobile keyboard
            keyboardProxyRef.current?.focus({ preventScroll: true });
            // Immediately open the centered text editor with black default
            setSelectedColor("#000000");
            setIsAddingText(true);
            setSelectedTextId(null);
        } else if (tool === "pen") {
            // Restore red default for pen
            setSelectedColor("#EF4444");
            setSelectedTextId(null);
            setIsAddingText(false);
        } else if (tool === "shapes") {
            setSelectedColor("#EF4444");
            setSelectedTextId(null);
            setIsAddingText(false);
        } else if (tool === "crop") {
            setViewTransform({ scale: 1, offsetX: 0, offsetY: 0 });
            const canvas = canvasRef.current;
            if (canvas) {
                setCropRect({ x: 0, y: 0, width: canvas.width, height: canvas.height });
            }
        } else {
            setSelectedTextId(null);
            setIsAddingText(false);
            setCropRect(null);
        }
    }, [isAddingText, editingTextId]);

    // Load image and setup canvas
    useEffect(() => {
        if (!isOpen || !imageFile) return;
        setDecodeError(false);

        // Guard against stale handlers: on cleanup we revoke the object URL,
        // which makes the old <img> fire onerror. Without this flag that late
        // onerror would flip decodeError for an image we've already replaced
        // (visible under StrictMode's double-mount and on rapid image switches).
        let cancelled = false;
        const img = new Image();
        const objectUrl = URL.createObjectURL(imageFile);
        imageSrcRef.current = objectUrl;

        img.onload = () => {
            if (cancelled) return;
            imageRef.current = img;
            setCanvasReady(true);
        };
        img.onerror = () => {
            if (cancelled) return;
            // Reached only if an undecodable image slips past ingestion conversion.
            // Surface it instead of leaving the canvas blank forever.
            setDecodeError(true);
        };

        img.src = objectUrl;

        return () => {
            cancelled = true;
            img.onload = null;
            img.onerror = null;
            if (imageSrcRef.current) {
                URL.revokeObjectURL(imageSrcRef.current);
                imageSrcRef.current = null;
            }
            imageRef.current = null;
            setCanvasReady(false);
        };
    }, [isOpen, imageFile]);

    // Setup canvas dimensions and render. Runs as a layout effect so the
    // text-overlay DOM uses the correct canvasScale on the same paint as the
    // canvas bitmap, avoiding a first-render where text overlays position
    // against a stale scale (only visible once the user taps the canvas).
    useLayoutEffect(() => {
        if (!canvasReady || !imageRef.current || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const container = containerRef.current;
        const img = imageRef.current;

        if (!container) return;

        // The canvas is the ROTATION-CORRECTED image: odd quarter-turns swap
        // its dimensions. Drawings are stored in this same space, which is why
        // pointer input and the text overlay layer need no rotation awareness.
        const corrected = getRotatedSize(img.naturalWidth, img.naturalHeight, rotation);
        canvas.width = corrected.width;
        canvas.height = corrected.height;

        const containerRect = container.getBoundingClientRect();
        const imageAspect = corrected.width / corrected.height;
        const containerAspect = containerRect.width / containerRect.height;

        let displayWidth: number;
        let displayHeight: number;

        if (imageAspect > containerAspect) {
            displayWidth = containerRect.width;
            displayHeight = containerRect.width / imageAspect;
        } else {
            displayHeight = containerRect.height;
            displayWidth = containerRect.height * imageAspect;
        }

        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;

        setCanvasScale(getCanvasScale(canvas));
        setCanvasSize({
            width: corrected.width,
            height: corrected.height,
            displayWidth,
            displayHeight,
        });

        const ctx = canvas.getContext("2d");
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // Only the image needs the rotation transform — the drawings are
            // already stored in the corrected space the canvas represents.
            ctx.save();
            applyRotationTransform(ctx, rotation, img.naturalWidth, img.naturalHeight);
            ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
            ctx.restore();
            renderNonTextDrawings(ctx, drawings, currentDrawing);
        }
    }, [canvasReady, drawings, currentDrawing, rotation]);

    const handlePointerDown = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            if (!canvasRef.current || isInlineEditing) return;
            if (selectedTool === "crop") return;
            if (isZoomingRef.current) return;
            // Don't start drawing on multi-touch (pinch gesture)
            if ("touches" in e && e.touches.length > 1) return;

            if ("touches" in e) {
                e.preventDefault();
            }

            const point = getPointFromEvent(e, canvasRef.current);
            // Divide by viewScale to counteract the CSS scale() transform,
            // keeping visual stroke thickness constant at any zoom level.
            const baseScale = getCanvasScale(canvasRef.current);
            const strokeScale = baseScale / viewTransform.scale;

            if (selectedTool === "text") {
                if (selectedTextId) {
                    // Tap on canvas deselects the selected text
                    setSelectedTextId(null);
                }
                return;
            }

            if (selectedTool === "pen") {
                setIsDrawing(true);
                setCurrentDrawing({
                    id: generateId(),
                    type: "pen",
                    color: selectedColor,
                    points: [point],
                    lineWidth: penThickness * strokeScale,
                });
            }

            if (selectedTool === "shapes") {
                setIsDrawing(true);
                setCurrentDrawing({
                    id: generateId(),
                    type: "shape",
                    shapeType: selectedShapeType,
                    color: selectedColor,
                    start: point,
                    end: point,
                    lineWidth: shapeThickness * strokeScale,
                });
            }
        },
        [selectedTool, selectedColor, selectedTextId, penThickness, shapeThickness, selectedShapeType, isInlineEditing, viewTransform.scale]
    );

    const handlePointerMove = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            // Stop drawing input during pinch-to-zoom (state may not have flushed yet)
            if (isZoomingRef.current) return;
            if (!isDrawing || !canvasRef.current || !currentDrawing) return;

            if ("touches" in e) {
                e.preventDefault();
            }

            const point = getPointFromEvent(e, canvasRef.current);

            if (currentDrawing.type === "pen") {
                setCurrentDrawing({
                    ...currentDrawing,
                    points: [...currentDrawing.points, point],
                } as PenDrawing);
            }

            if (currentDrawing.type === "shape") {
                setCurrentDrawing({
                    ...currentDrawing,
                    end: point,
                } as ShapeDrawing);
            }
        },
        [isDrawing, currentDrawing]
    );

    const handlePointerUp = useCallback(() => {
        if (!isDrawing || !currentDrawing) {
            setIsDrawing(false);
            return;
        }

        if (currentDrawing.type === "pen" && currentDrawing.points.length >= 2) {
            addDrawing(currentDrawing);
        }

        if (currentDrawing.type === "shape") {
            const dx = currentDrawing.end.x - currentDrawing.start.x;
            const dy = currentDrawing.end.y - currentDrawing.start.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                addDrawing(currentDrawing);
            }
        }

        setCurrentDrawing(null);
        setIsDrawing(false);
    }, [isDrawing, currentDrawing, addDrawing]);

    // Text overlay callbacks
    const handleTextSelect = useCallback((id: string) => {
        setSelectedTextId(id);
    }, []);

    const handleTextDragEnd = useCallback(
        (id: string, newPosition: Point) => {
            commitDrawingUpdate(id, (d) => ({
                ...d,
                position: newPosition,
            }) as Drawing);
        },
        [commitDrawingUpdate]
    );

    const handleTextActivate = useCallback((id: string) => {
        setSelectedTool("text");
        setSelectedTextId(id);
        setIsAddingText(false); // Don't open editor, just select
    }, []);

    const handleTextTapToEdit = useCallback(
        (id: string) => {
            const drawing = textDrawings.find((d) => d.id === id);
            if (!drawing) return;
            setEditingTextId(id);
        },
        [textDrawings]
    );

    // Inline text confirm — new text or editing existing
    const handleInlineTextConfirm = useCallback(
        (text: string) => {
            if (editingTextId) {
                commitDrawingUpdate(editingTextId, (d) => ({
                    ...d,
                    text,
                }) as Drawing);
                setEditingTextId(null);
                setSelectedTool(null);
                return;
            }

            // New text — place at center of canvas
            const canvas = canvasRef.current;
            if (!canvas) return;

            const scale = getCanvasScale(canvas);
            const canvasFontSize = fontSize * scale;
            // maxWidth matches the 80% constraint from the inline editor
            const canvasDisplayWidth = parseFloat(canvas.style.width || "300");
            const textMaxWidth = canvasDisplayWidth * 0.8 * scale;
            const centerPosition: Point = {
                x: canvas.width / 2 - textMaxWidth / 2,
                y: canvas.height / 2 - canvasFontSize,
            };

            const textDrawing: TextDrawing = {
                id: generateId(),
                type: "text",
                color: selectedColor,
                position: centerPosition,
                text,
                fontSize: canvasFontSize,
                maxWidth: textMaxWidth,
            };

            addDrawing(textDrawing);
            setIsAddingText(false);
            setSelectedTextId(null);
            // Exit text mode immediately after confirming
            setSelectedTool(null);
        },
        [editingTextId, selectedColor, fontSize, addDrawing, commitDrawingUpdate]
    );

    const handleInlineTextCancel = useCallback(() => {
        setIsAddingText(false);
        setEditingTextId(null);
        // If was adding new text, exit text tool
        if (!editingTextId) {
            setSelectedTool(null);
        }
    }, [editingTextId]);

    // Color change handler
    const handleColorChange = useCallback(
        (newColor: string) => {
            setSelectedColor(newColor);
            if (selectedTextId) {
                commitDrawingUpdate(selectedTextId, (d) => ({
                    ...d,
                    color: newColor,
                }) as Drawing);
            }
        },
        [selectedTextId, commitDrawingUpdate]
    );

    // Render the current drawings + crop into a JPEG payload. Shared by Done
    // (which then calls `onComplete`) and the imperative `commitToPayload`
    // handle (which returns it to the host for commit-on-switch). Returns null
    // when there is no image to render.
    const buildPayload = useCallback(async (): Promise<MarkupCompletePayload | null> => {
        if (!imageFile) return null;

        setIsExporting(true);
        setExportError(null);
        try {
            // Flatten drawings + crop into a JPEG blob client-side. The server
            // stores these bytes directly as ProjectImage.image — no Pillow
            // render round-trip, so the optimistic tile matches the final
            // image immediately on submit. The same composite pass also emits
            // a small thumbnail so a host can repaint a tile without decoding
            // the full-resolution result.
            const { annotatedBlob, thumbnailBlob } = await renderAnnotatedPair({
                file: imageFile,
                drawings,
                crop: cropRect ?? undefined,
                rotation,
            });
            const payload: MarkupCompletePayload = {
                originalFile: imageFile,
                annotatedBlob,
                thumbnailBlob,
                drawings,
                crop: cropRect ?? undefined,
                // Omit the no-op so records that were never rotated keep the
                // exact annotations shape they had before rotation shipped.
                rotation: rotation === 0 ? undefined : rotation,
            };
            return payload;
        } finally {
            setIsExporting(false);
        }
    }, [imageFile, drawings, cropRect, rotation]);

    // A render throw here used to reject unhandled: no `onComplete`, no error,
    // no report — a Done button that simply looked dead. Decode failures and
    // canvas allocation refusals on large photos are real on low-memory
    // devices, so surface and report instead of swallowing.
    const handleExport = useCallback(async () => {
        try {
            const payload = await buildPayload();
            if (payload) onComplete(payload);
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Couldn't save your markups. Please try again.";
            console.error("Markup render failed:", error);
            Sentry.captureException(error, {
                level: "error",
                tags: { area: "markup", phase: "render" },
            });
            setExportError(message);
        }
    }, [buildPayload, onComplete]);

    // Expose `commitToPayload` so the burst-camera host can persist in-progress
    // edits before switching filmstrip photos, without pressing Done.
    useImperativeHandle(ref, () => ({ commitToPayload: buildPayload }), [buildPayload]);

    const handleCloseAttempt = useCallback(() => {
        setIsDiscardDialogOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        clearHistory();
        setCurrentDrawing(null);
        setIsDrawing(false);
        setSelectedTextId(null);
        setEditingTextId(null);
        setIsAddingText(false);
        setSelectedTool(null);
        onClose();
    }, [clearHistory, onClose]);

    // Lazily-created offscreen context, used only to measure text boxes when
    // rotating. Kept off the visible canvas so measuring can't disturb it.
    const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    const measureTextSize = useCallback((drawing: TextDrawing) => {
        if (!measureCtxRef.current) {
            measureCtxRef.current = document.createElement("canvas").getContext("2d");
        }
        const ctx = measureCtxRef.current;
        return ctx ? measureTextDrawing(ctx, drawing) : null;
    }, []);

    /**
     * Apply N clockwise quarter-turns to the whole editing space at once.
     *
     * Drawings live in rotation-corrected space, so they have to be re-mapped —
     * across EVERY history snapshot, or an Undo after a rotation would restore
     * coordinates from the previous orientation. The crop rect turns with them
     * so it keeps framing the same content.
     */
    const applyQuarterTurns = useCallback(
        (turns: number) => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const n = normalizeTurns(turns);
            if (n === 0) return;

            // The canvas IS the current drawing space, so its dimensions are
            // the space we're turning out of.
            const spaceW = canvas.width;
            const spaceH = canvas.height;

            transformAll((list) => turnDrawings(list, n, spaceW, spaceH, measureTextSize));
            setCropRect((prev) => (prev ? turnRectBy(prev, n, spaceW, spaceH) : prev));
            setRotation((prev) => addTurns(prev, n));
        },
        [transformAll, measureTextSize],
    );

    // Rotate is a one-tap action in the main top bar, not part of the crop
    // session: the turn is kept immediately and the selected tool is untouched.
    const handleRotate = useCallback(() => {
        // Commit (or drop, if empty) any open inline text first so it enters
        // history and gets turned with every other drawing.
        if (isAddingText || editingTextId) {
            inlineTextEditorRef.current?.confirmOrCancel();
        }
        // The canvas swaps dimensions on the turn; a zoom/pan from the old
        // orientation would leave the new one off-screen.
        setViewTransform({ scale: 1, offsetX: 0, offsetY: 0 });
        applyQuarterTurns(1);
    }, [isAddingText, editingTextId, applyQuarterTurns]);

    const handleCropConfirm = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !cropRect) return;

        // No-op: crop covers the full image — treat as clearing the crop
        const isFullImage =
            Math.round(cropRect.x) === 0 &&
            Math.round(cropRect.y) === 0 &&
            Math.round(cropRect.width) === canvas.width &&
            Math.round(cropRect.height) === canvas.height;

        if (isFullImage) {
            setCropRect(null);
            setSelectedTool(null);
            return;
        }

        // Crop rect is stored as-is (in original-image coordinates).
        // The underlying imageRef/imageFile are unchanged — the server applies
        // the crop when rendering the annotated derivative.
        // cropRect state already holds the confirmed rect; just exit crop mode.
        setSelectedTool(null);
    }, [cropRect]);

    const handleCropCancel = useCallback(() => {
        setCropRect(null);
        setSelectedTool(null);
    }, []);

    // Clamp helper for zoom transforms
    const clampTransform = useCallback((scale: number, offsetX: number, offsetY: number): ViewTransform => {
        const clampedScale = Math.min(Math.max(scale, 1), 5);
        if (clampedScale <= 1) {
            return { scale: 1, offsetX: 0, offsetY: 0 };
        }
        const container = containerRef.current;
        const maxOffset = container ? Math.max(container.clientWidth, container.clientHeight) * 0.4 * clampedScale : 200;
        return {
            scale: clampedScale,
            offsetX: Math.min(Math.max(offsetX, -maxOffset), maxOffset),
            offsetY: Math.min(Math.max(offsetY, -maxOffset), maxOffset),
        };
    }, []);

    // Touch gesture handling: pinch-to-zoom and 1-finger pan
    // Uses refs for viewTransform and selectedTool to avoid re-registering handlers
    // on every zoom change (which caused missed events and stale closures).
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        function getTouchDistance(t1: Touch, t2: Touch): number {
            const dx = t1.clientX - t2.clientX;
            const dy = t1.clientY - t2.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        }

        function handleTouchStart(e: TouchEvent) {
            if (isCropMode) return;

            if (e.touches.length >= 2) {
                const t1 = e.touches[0];
                const t2 = e.touches[1];
                const initialDistance = getTouchDistance(t1, t2);

                // If a text is selected and no tool is active, route pinch to text resize
                // BUT only when at least one finger starts on (or within) the text overlay element.
                const textId = selectedTextIdRef.current;
                if (textId && selectedToolRef.current === null) {
                    // Read the drawing's current fontSize via ref (avoids stale closure)
                    const drawing = drawingsRef.current.find((d) => d.id === textId);
                    if (drawing && drawing.type === "text") {
                        // Hit-test: at least one finger must be inside the text overlay's bounding rect
                        const textEl = container?.querySelector<HTMLElement>(`[data-text-overlay-id="${textId}"]`);
                        const fingerOnText = (() => {
                            if (!textEl) return false;
                            const rect = textEl.getBoundingClientRect();
                            const touches = [t1, t2];
                            return touches.some(
                                (t) =>
                                    t.clientX >= rect.left &&
                                    t.clientX <= rect.right &&
                                    t.clientY >= rect.top &&
                                    t.clientY <= rect.bottom
                            );
                        })();

                        if (fingerOnText) {
                            textResizePinchRef.current = {
                                initialDistance,
                                initialFontSize: drawing.fontSize,
                                initialMaxWidth: drawing.maxWidth,
                                textId,
                                currentScale: 1,
                                // Capture natural (un-scaled) display height so we can
                                // keep the visual center fixed when we commit on release.
                                initialDisplayHeight: textEl ? textEl.offsetHeight : 0,
                            };
                            isZoomingRef.current = true;
                            e.preventDefault();
                            return;
                        }
                        // Finger not on text — fall through to canvas zoom below
                    }
                }

                // No text selected (or not found) — start canvas pinch-zoom
                // Complete any in-progress drawing before zooming
                isZoomingRef.current = true;
                const drawing = currentDrawingRef.current;
                if (drawing) {
                    if (drawing.type === "pen" && drawing.points.length >= 2) {
                        addDrawingRef.current(drawing);
                    } else if (drawing.type === "shape") {
                        const dx = drawing.end.x - drawing.start.x;
                        const dy = drawing.end.y - drawing.start.y;
                        if (Math.sqrt(dx * dx + dy * dy) > 5) {
                            addDrawingRef.current(drawing);
                        }
                    }
                }
                setCurrentDrawing(null);
                setIsDrawing(false);

                const vt = viewTransformRef.current;
                pinchRef.current = {
                    initialDistance,
                    initialScale: vt.scale,
                    midX: (t1.clientX + t2.clientX) / 2,
                    midY: (t1.clientY + t2.clientY) / 2,
                    initialOffsetX: vt.offsetX,
                    initialOffsetY: vt.offsetY,
                };
                e.preventDefault();
                return;
            }

            // 1-finger: double-tap detection
            if (e.touches.length === 1) {
                const now = Date.now();
                const tool = selectedToolRef.current;
                const vt = viewTransformRef.current;
                if (now - lastTapTimeRef.current < 300 && tool === null && vt.scale > 1) {
                    setViewTransform({ scale: 1, offsetX: 0, offsetY: 0 });
                    lastTapTimeRef.current = 0;
                    e.preventDefault();
                    return;
                }
                lastTapTimeRef.current = now;

                // 1-finger pan: only when no tool active and zoomed in
                if (tool === null && vt.scale > 1) {
                    panRef.current = {
                        startX: e.touches[0].clientX,
                        startY: e.touches[0].clientY,
                        startOffsetX: vt.offsetX,
                        startOffsetY: vt.offsetY,
                    };
                }
            }
        }

        function handleTouchMove(e: TouchEvent) {
            if (isCropMode) return;

            if (e.touches.length >= 2 && textResizePinchRef.current) {
                e.preventDefault();
                const t1 = e.touches[0];
                const t2 = e.touches[1];
                const currentDistance = getTouchDistance(t1, t2);
                const ratio = currentDistance / textResizePinchRef.current.initialDistance;
                textResizePinchRef.current.currentScale = ratio;

                // Apply live preview via CSS transform — no React state update, no re-render
                const el = container?.querySelector<HTMLElement>(
                    `[data-text-overlay-id="${textResizePinchRef.current.textId}"]`
                );
                if (el) {
                    el.style.transform = `scale(${ratio})`;
                    el.style.transformOrigin = "center center";
                }
                return;
            }

            if (e.touches.length >= 2 && pinchRef.current) {
                e.preventDefault();
                const t1 = e.touches[0];
                const t2 = e.touches[1];
                const currentDistance = getTouchDistance(t1, t2);
                const ratio = currentDistance / pinchRef.current.initialDistance;
                const newScale = pinchRef.current.initialScale * ratio;

                // Compute offset to keep focal midpoint fixed
                const currentMidX = (t1.clientX + t2.clientX) / 2;
                const currentMidY = (t1.clientY + t2.clientY) / 2;
                const newOffsetX = pinchRef.current.initialOffsetX + (currentMidX - pinchRef.current.midX);
                const newOffsetY = pinchRef.current.initialOffsetY + (currentMidY - pinchRef.current.midY);

                setViewTransform(clampTransform(newScale, newOffsetX, newOffsetY));
                return;
            }

            if (e.touches.length === 1 && panRef.current) {
                e.preventDefault();
                const dx = e.touches[0].clientX - panRef.current.startX;
                const dy = e.touches[0].clientY - panRef.current.startY;
                setViewTransform((prev) => clampTransform(prev.scale, panRef.current!.startOffsetX + dx, panRef.current!.startOffsetY + dy));
            }
        }

        function handleTouchEnd(e: TouchEvent) {
            if (e.touches.length < 2) {
                pinchRef.current = null;

                if (textResizePinchRef.current) {
                    const { textId, initialFontSize, initialMaxWidth, currentScale, initialDisplayHeight } = textResizePinchRef.current;
                    const rawFontSize = Math.round(initialFontSize * currentScale);
                    const clampedFontSize = Math.max(12, Math.min(rawFontSize, MAX_CANVAS_FONT_SIZE));
                    // Scale maxWidth by the same ratio so line-breaks are preserved (uniform scale)
                    const scaledMaxWidth = Math.round(initialMaxWidth * (clampedFontSize / initialFontSize));
                    // The actual scale applied (may differ from currentScale when font size is clamped)
                    const actualScaleFactor = clampedFontSize / initialFontSize;

                    // Clear the temporary CSS transform before committing
                    const el = container?.querySelector<HTMLElement>(`[data-text-overlay-id="${textId}"]`);
                    if (el) {
                        el.style.transform = "";
                        el.style.transformOrigin = "";
                    }

                    // Canvas-pixel : display-pixel ratio, used to convert offsetHeight → canvas coords
                    const cs = canvasRef.current ? getCanvasScale(canvasRef.current) : 1;

                    commitDrawingUpdate(textId, (d) => {
                        const dText = d as TextDrawing;
                        // The CSS scale(ratio) transform kept the element's CENTER fixed.
                        // After clearing it, the top-left stays at drawing.position while
                        // the natural size changes — so the center shifts, causing the jump.
                        // Compensate by shifting position so the center is preserved.
                        //
                        // Horizontal: center_x = position.x + maxWidth/2
                        //   newPositionX = position.x + (oldMaxWidth - newMaxWidth) / 2
                        const adjustedX = dText.position.x + (dText.maxWidth - scaledMaxWidth) / 2;
                        //
                        // Vertical: element height scales proportionally with fontSize.
                        //   center_y = position.y + (displayHeight * cs) / 2
                        //   newPositionY = position.y + (oldHeight - newHeight) * cs / 2
                        //               = position.y + initialDisplayHeight * cs * (1 - actualScaleFactor) / 2
                        const adjustedY = dText.position.y + (initialDisplayHeight * cs * (1 - actualScaleFactor)) / 2;
                        return {
                            ...dText,
                            fontSize: clampedFontSize,
                            maxWidth: scaledMaxWidth,
                            position: { x: adjustedX, y: adjustedY },
                        } as Drawing;
                    });

                    textResizePinchRef.current = null;
                }
            }
            if (e.touches.length === 0) {
                // Only clear zoom flag when all fingers lift so a remaining
                // finger after pinch doesn't immediately start a new stroke
                requestAnimationFrame(() => {
                    isZoomingRef.current = false;
                });
                panRef.current = null;
            }
        }

        container.addEventListener("touchstart", handleTouchStart, { passive: false });
        container.addEventListener("touchmove", handleTouchMove, { passive: false });
        container.addEventListener("touchend", handleTouchEnd);
        container.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            container.removeEventListener("touchstart", handleTouchStart);
            container.removeEventListener("touchmove", handleTouchMove);
            container.removeEventListener("touchend", handleTouchEnd);
            container.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [isCropMode, clampTransform, containerMounted, commitDrawingUpdate]);

    // Desktop scroll-wheel zoom
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        function handleWheel(e: WheelEvent) {
            if (isCropMode) return;
            e.preventDefault();

            const factor = e.deltaY > 0 ? 0.9 : 1.1;
            setViewTransform((prev) => {
                const newScale = prev.scale * factor;
                // Zoom toward cursor position
                const rect = container!.getBoundingClientRect();
                const cursorX = e.clientX - rect.left - rect.width / 2;
                const cursorY = e.clientY - rect.top - rect.height / 2;
                const newOffsetX = prev.offsetX - cursorX * (factor - 1);
                const newOffsetY = prev.offsetY - cursorY * (factor - 1);
                return clampTransform(newScale, newOffsetX, newOffsetY);
            });
        }

        container.addEventListener("wheel", handleWheel, { passive: false });
        return () => container.removeEventListener("wheel", handleWheel);
    }, [isCropMode, clampTransform, containerMounted]);

    // Get the text being edited (for inline editor initialText)
    const editingTextDrawing = editingTextId
        ? textDrawings.find((d) => d.id === editingTextId)
        : null;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => !open && handleCloseAttempt()}
        >
            <DialogPortal>
                <DialogOverlay />
                <DialogPrimitive.Content
                    className="fixed inset-x-0 top-0 h-dvh z-[60] flex flex-col bg-black outline-none"
                >
                    <DialogPrimitive.Title className="sr-only">
                        Edit Image
                    </DialogPrimitive.Title>

                    <TopBar
                        onClose={handleCloseAttempt}
                        onUndo={undo}
                        canUndo={canUndo}
                        selectedTool={selectedTool}
                        selectedColor={selectedColor}
                        onToolChange={handleToolChange}
                        onCropConfirm={handleCropConfirm}
                        onCropCancel={handleCropCancel}
                        onRotate={handleRotate}
                    />

                    {/* Hidden proxy input — focused during tap to keep mobile keyboard raised */}
                    <input
                        ref={keyboardProxyRef}
                        aria-hidden
                        tabIndex={-1}
                        style={{ position: "absolute", opacity: 0, height: 0, width: 0, overflow: "hidden", pointerEvents: "none" }}
                    />

                    {/* Canvas Container */}
                    <div
                        ref={containerCallbackRef}
                        className="flex-1 flex items-center justify-center overflow-hidden touch-none relative"
                    >
                        {decodeError && (
                            <p className="text-white/50 text-sm font-medium px-6 text-center">
                                This image couldn&apos;t be opened.
                            </p>
                        )}
                        <div
                            ref={canvasWrapperRef}
                            className={cn("relative", !isCropMode && "overflow-hidden")}
                            style={{
                                transform: isCropMode
                                    ? "scale(0.85)"
                                    : `translate(${viewTransform.offsetX}px, ${viewTransform.offsetY}px) scale(${viewTransform.scale})`,
                                transformOrigin: "center center",
                                transition: isCropMode ? "transform 0.2s ease" : undefined,
                            }}
                        >
                            <canvas
                                ref={canvasRef}
                                onMouseDown={handlePointerDown}
                                onMouseMove={handlePointerMove}
                                onMouseUp={handlePointerUp}
                                onMouseLeave={handlePointerUp}
                                onTouchStart={handlePointerDown}
                                onTouchMove={handlePointerMove}
                                onTouchEnd={handlePointerUp}
                                onTouchCancel={handlePointerUp}
                                className="touch-none"
                                style={{ touchAction: "none" }}
                            />

                            {/* Text overlay layer */}
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{ touchAction: "none" }}
                            >
                                {textDrawings.map((td) => {
                                    // Hide the text overlay when it's being inline-edited
                                    if (editingTextId === td.id) return null;
                                    return (
                                        <TextOverlay
                                            key={td.id}
                                            drawing={td}
                                            canvasScale={canvasScale}
                                            viewScale={viewTransform.scale}
                                            isSelected={selectedTextId === td.id}
                                            interactable={selectedTool === null}
                                            boundsRef={canvasWrapperRef}
                                            onSelect={handleTextSelect}
                                            onDragEnd={handleTextDragEnd}
                                            onTapToEdit={handleTextTapToEdit}
                                            onActivate={handleTextActivate}
                                        />
                                    );
                                })}
                            </div>

                            {/* Crop overlay */}
                            {isCropMode && cropRect && canvasSize && (
                                <CropOverlay
                                    cropRect={cropRect}
                                    onCropChange={setCropRect}
                                    canvasWidth={canvasSize.width}
                                    canvasHeight={canvasSize.height}
                                    displayWidth={canvasSize.displayWidth}
                                    displayHeight={canvasSize.displayHeight}
                                />
                            )}
                        </div>

                        {/* Inline text editor — outside canvasWrapper to avoid zoom transforms */}
                        {!isCropMode && isAddingText && !editingTextId && (
                            <InlineTextEditor
                                ref={inlineTextEditorRef}
                                color={selectedColor}
                                suppressBlurRef={suppressBlurRef}
                                onConfirm={handleInlineTextConfirm}
                                onCancel={handleInlineTextCancel}
                            />
                        )}

                        {!isCropMode && editingTextDrawing && (
                            <InlineTextEditor
                                ref={inlineTextEditorRef}
                                color={editingTextDrawing.color}
                                initialText={editingTextDrawing.text}
                                suppressBlurRef={suppressBlurRef}
                                onConfirm={handleInlineTextConfirm}
                                onCancel={handleInlineTextCancel}
                            />
                        )}
                    </div>

                    {/* Color slider — positioned relative to the fixed dialog, immune to keyboard/layout shifts */}
                    {selectedTool !== null && selectedTool !== "crop" && (
                        <ColorSlider
                            selectedColor={selectedColor}
                            onColorChange={handleColorChange}
                            suppressBlurRef={suppressBlurRef}
                        />
                    )}

                    {/* Filmstrip navigator (burst camera) — between the stage and
                        the bottom controls; hidden while cropping like the bottom bar. */}
                    {filmstripSlot && !isCropMode && filmstripSlot}

                    {/* Bottom bar — two rows: sub-controls + persistent Done button */}
                    <div
                        className="bg-black px-4 pt-4 flex flex-col gap-3 z-10"
                        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 1rem))" }}
                    >
                        {/* Row 1: sub-controls — always reserve space to prevent canvas shift */}
                        <div className={cn(
                            "h-11 flex items-center w-full",
                            !(selectedTool === "pen" || selectedTool === "shapes") && "invisible"
                        )}>
                            {selectedTool === "pen" ? (
                                <ThicknessBar
                                    selectedThickness={penThickness}
                                    onThicknessChange={setPenThickness}
                                />
                            ) : (
                                <ShapePickerBar
                                    selectedShapeType={selectedShapeType}
                                    onShapeTypeChange={setSelectedShapeType}
                                    selectedThickness={shapeThickness}
                                    onThicknessChange={setShapeThickness}
                                />
                            )}
                        </div>

                        {exportError && (
                            <p
                                role="status"
                                aria-live="polite"
                                className="px-1 pb-2 text-xs text-red-400"
                            >
                                {exportError}
                            </p>
                        )}

                        {/* Row 2: Done button — always visible */}
                        <div className="flex items-center justify-between">
                            {projectName ? (
                                <span className="text-white/50 text-xs font-medium truncate max-w-[55%]">
                                    {projectName}
                                </span>
                            ) : (
                                <div />
                            )}
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={isExporting}
                                className={cn(
                                    "h-10 px-5 rounded-full bg-primary text-primary-foreground font-medium text-sm flex items-center gap-1.5 shrink-0",
                                    isExporting && "opacity-50 cursor-not-allowed"
                                )}
                                aria-label="Done"
                            >
                                Done
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPortal>

            <AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard photo?</AlertDialogTitle>
                        <AlertDialogDescription>Your annotations will be lost.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row gap-3 sm:gap-3">
                        <AlertDialogCancel className="flex-1 h-12">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="flex-1 h-12 bg-white text-red-500 hover:bg-red-50 border border-border shadow-none font-medium rounded-md"
                            onClick={handleClose}
                        >
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
});
